import { getDisplayColumnPrefix } from './periodUtils';
import { buildTableColumnTypeMap } from './excelExport';
import { canonicalizeTagValue, isEmptyTypedValue, normalizeCategoryType } from './tagValueUtils';

const ALL_COLUMN_TYPES = ['Text', 'Number', 'Date', 'Yes/No'];

const CONDITION_OPERATORS = {
  always: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  is_empty: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  is_not_empty: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  equals: { types: ALL_COLUMN_TYPES, operandCount: 1, supportsColumnOperand: true },
  not_equals: { types: ALL_COLUMN_TYPES, operandCount: 1, supportsColumnOperand: true },
  contains: { types: ['Text'], operandCount: 1 },
  not_contains: { types: ['Text'], operandCount: 1 },
  greater_than: { types: ['Number', 'Date'], operandCount: 1, supportsColumnOperand: true },
  less_than: { types: ['Number', 'Date'], operandCount: 1, supportsColumnOperand: true },
  greater_than_or_equal: { types: ['Number', 'Date'], operandCount: 1, supportsColumnOperand: true },
  less_than_or_equal: { types: ['Number', 'Date'], operandCount: 1, supportsColumnOperand: true },
  between: { types: ['Number', 'Date'], operandCount: 2, supportsColumnOperand: true },
  is_date: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  is_number: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  is_text: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  is_boolean: { types: ALL_COLUMN_TYPES, operandCount: 0 },
  length_equals: { types: ['Text'], operandCount: 1 },
  length_greater_than: { types: ['Text'], operandCount: 1 },
  length_greater_than_or_equal: { types: ['Text'], operandCount: 1 },
  length_less_than: { types: ['Text'], operandCount: 1 },
  length_less_than_or_equal: { types: ['Text'], operandCount: 1 },
  starts_with: { types: ['Text'], operandCount: 1 },
  ends_with: { types: ['Text'], operandCount: 1 },
  matches_regex: { types: ['Text'], operandCount: 1 },
  is_contained_in: { types: ALL_COLUMN_TYPES, operandCount: 1 },
};

// The simple cell condition, as built by CellConditionBuilder and read by
// ingestion/rules.py::_normalize_legacy_cell_condition.
const CELL_CONDITION_TYPES = new Set([
  'always', 'is_empty', 'content_length', 'content_type', 'contains', 'is_contained_in', 'numeric_comparison',
]);
const CELL_CONDITION_OPERATORS = new Set(['==', '<', '>', '<=', '>=', '!=']);
const CONTENT_TYPES = new Set(['date', 'number', 'text']);

// Which declared column types each simple cell condition can actually run on.
// _normalize_legacy_cell_condition maps every one of these onto an operator in
// CONDITION_OPERATORS above, and column_types.py::_validate_condition_type_support
// RAISES when the column's declared type does not support that operator — failing
// the unit, and with it every upload into the table, until someone works out which
// rule to disable. The editor used to offer all seven for any column, so the rule
// saved cleanly and only broke at the next upload.
//
// Numeric Comparison is listed for every type because its equality operators are
// universal; only its ordering operators need Number or Date (see
// cellConditionOperators).
export const CELL_CONDITION_LABELS = {
  always: 'Always',
  is_empty: 'Is Empty',
  content_length: 'Content Length',
  content_type: 'Content Type',
  contains: 'Contains',
  is_contained_in: 'Is Contained In',
  numeric_comparison: 'Numeric Comparison',
};
const CELL_CONDITION_COLUMN_TYPES = {
  always: ALL_COLUMN_TYPES,
  is_empty: ALL_COLUMN_TYPES,
  content_length: ['Text'],        // -> length_equals & friends, Text-only operators
  content_type: ALL_COLUMN_TYPES,  // -> is_date / is_number / is_text / is_boolean
  contains: ['Text'],              // -> contains, a Text-only operator
  is_contained_in: ALL_COLUMN_TYPES,
  numeric_comparison: ALL_COLUMN_TYPES,
};
const ORDERABLE_COLUMN_TYPES = ['Number', 'Date'];
const EQUALITY_CELL_OPERATORS = ['==', '!='];
const ORDERING_CELL_OPERATORS = ['<', '>', '<=', '>='];

export const cellConditionTypesForColumn = columnType => (
  Object.keys(CELL_CONDITION_COLUMN_TYPES)
    .filter(type => CELL_CONDITION_COLUMN_TYPES[type].includes(columnType))
);

export const cellConditionOperators = (conditionType, columnType) => {
  // Content Length compares a length, which is always a number — every operator is
  // legal once the column is Text, and Text is the only type it is offered for.
  if (conditionType === 'content_length') {
    return [...EQUALITY_CELL_OPERATORS, ...ORDERING_CELL_OPERATORS];
  }
  // Numeric Comparison compares the value itself: '==' and '!=' map onto
  // equals/not_equals (every type), the other four onto greater_than and friends,
  // which column_types.py restricts to Number and Date.
  return ORDERABLE_COLUMN_TYPES.includes(columnType)
    ? [...EQUALITY_CELL_OPERATORS, ...ORDERING_CELL_OPERATORS]
    : [...EQUALITY_CELL_OPERATORS];
};

const ACTION_TYPES = new Set([
  'skip_row', 'fail_row', 'warn_row', 'trim', 'remove_spaces', 'set_value', 'concatenate', 'transform_value',
]);
const TARGET_REQUIRED_ACTIONS = new Set(['trim', 'remove_spaces', 'set_value', 'concatenate', 'transform_value']);
const TEXT_ONLY_ACTIONS = new Set(['trim', 'remove_spaces', 'concatenate']);

// The transforms Concatenate can run: they take no parameters, which is all the
// runtime passes it (_apply_typed_action calls _apply_transform with an empty
// params dict), and they are exactly what its own Transform menu offers.
const CONCATENATE_TRANSFORMS = new Set(['uppercase', 'lowercase', 'trim']);

const TRANSFORMS_BY_TYPE = {
  Date: new Set(['format_date']),
  Number: new Set(['round_number']),
  Text: new Set([
    'uppercase', 'lowercase', 'trim', 'remove_spaces', 'strip_leading_zeros', 'keep_digits',
    'remove_characters', 'left', 'right', 'mid', 'replace', 'pad_left', 'pad_right',
    'drop_prefix', 'drop_suffix', 'cast_number_string',
  ]),
};

// Parameters a transformation cannot run without: [parameter, kind, what to ask for].
// A count of zero is not a default, it blanks the cell (Left with N=0 keeps nothing).
const TRANSFORM_PARAMETERS = {
  remove_characters: [['characters', 'text', 'the characters to remove']],
  left: [['count', 'count', 'a count of at least 1']],
  right: [['count', 'count', 'a count of at least 1']],
  mid: [['length', 'count', 'a length of at least 1']],
  replace: [['find', 'text', 'the text to find']],
  pad_left: [['length', 'count', 'a target length of at least 1']],
  pad_right: [['length', 'count', 'a target length of at least 1']],
  drop_prefix: [['text', 'text', 'the prefix to drop']],
  drop_suffix: [['text', 'text', 'the suffix to drop']],
};

const FORMULA_TYPES_BY_TARGET = {
  Text: ['prepend', 'append'],
  Date: ['arithmetic'],
  Number: ['arithmetic'],
  'Yes/No': [],
};
const ARITHMETIC_OPERATIONS = {
  Date: ['add', 'subtract'],
  Number: ['multiply', 'divide', 'add', 'subtract'],
};

const rulesToArray = rules => {
  if (Array.isArray(rules)) return rules.filter(Boolean);
  if (!rules || typeof rules !== 'object') return [];
  return Object.keys(rules)
    .sort((left, right) => Number(left) - Number(right))
    .map(key => rules[key])
    .filter(Boolean);
};

const buildRuleColumnTypeMap = tableSchema => {
  const typeMap = buildTableColumnTypeMap(tableSchema);
  if (!tableSchema) return typeMap;
  const periodPrefix = getDisplayColumnPrefix(tableSchema);
  if (periodPrefix) typeMap.set(periodPrefix, 'Date');
  return typeMap;
};

const isMissingOperand = value => (
  isEmptyTypedValue(value)
  || (Array.isArray(value) && value.length === 0)
);

const isNumeric = value => (
  (typeof value === 'number' && Number.isFinite(value))
  || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)))
);

const isCharacterCount = value => (
  (typeof value === 'number' && Number.isInteger(value) && value >= 0)
  || (typeof value === 'string' && /^\+?[0-9]+$/.test(value.trim()) && Number.isFinite(Number(value)))
);

const validateOperand = (
  condition,
  suffix,
  operatorConfig,
  leftColumnType,
  columnTypeMap,
) => {
  const operandKey = suffix ? 'rhs2' : 'rhs1';
  const nestedOperand = condition[operandKey] || {};
  const sourceField = `compare_source${suffix}`;
  const columnField = `compare_column${suffix}`;
  const valueField = `compare_value${suffix}`;
  const legacyValueField = `value${suffix}`;
  const comparisonColumn = nestedOperand.column || condition[columnField];
  const source = nestedOperand.source
    || condition[sourceField]
    || (comparisonColumn ? 'column' : 'literal');

  if (source === 'column') {
    if (!operatorConfig.supportsColumnOperand) {
      return 'does not support a comparison column';
    }
    if (!comparisonColumn || !columnTypeMap.has(comparisonColumn)) {
      return 'must select a valid comparison column';
    }
    if (normalizeCategoryType(columnTypeMap.get(comparisonColumn)) !== leftColumnType) {
      return `comparison column "${comparisonColumn}" must also be ${leftColumnType}`;
    }
    return null;
  }

  if (source !== 'literal') return 'has an invalid comparison source';
  const value = nestedOperand.value
    ?? condition[valueField]
    ?? condition[legacyValueField];
  if (isMissingOperand(value)) return 'must provide a comparison value';
  if (condition.operator.startsWith('length_')) {
    if (!isCharacterCount(value)) return 'must provide a non-negative whole number of characters';
  }
  if (operatorConfig.supportsColumnOperand) {
    if (typeof value === 'object') return 'must provide a scalar comparison value';
    if (leftColumnType === 'Number') {
      const numericText = typeof value === 'string'
        && /^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/.test(value.trim());
      if (!(typeof value === 'number' || numericText) || !Number.isFinite(Number(value))) {
        return 'must provide a valid Number comparison value';
      }
    }
    if (leftColumnType === 'Yes/No' && typeof value !== 'boolean') {
      return 'must select a Yes/No comparison value';
    }
    if (leftColumnType === 'Date') {
      try {
        canonicalizeTagValue(value, 'Date');
      } catch (error) {
        return 'must provide a valid Date comparison value';
      }
    }
  }
  if (condition.operator === 'matches_regex') {
    // The runtime compiles the pattern with Python's re, which is the authority; this
    // catches the common breakage, an unclosed bracket, before it fails every upload (H80).
    try {
      new RegExp(String(value));
    } catch (error) {
      return 'must be a valid regular expression';
    }
  }
  return null;
};

const validateConditions = (conditions, label, columnTypeMap) => {
  if (conditions.length === 0) {
    return `${label} must contain at least one condition.`;
  }

  for (let conditionIndex = 0; conditionIndex < conditions.length; conditionIndex += 1) {
    const condition = conditions[conditionIndex] || {};
    const conditionLabel = `${label}, condition ${conditionIndex + 1}`;
    const column = condition.column;
    if (!column) return `${conditionLabel} must select a column.`;
    if (!columnTypeMap.has(column)) {
      return `${conditionLabel} references column "${column}", which is not in this table.`;
    }

    const operatorConfig = CONDITION_OPERATORS[condition.operator];
    if (!operatorConfig) return `${conditionLabel} must select a valid operator.`;
    const columnType = normalizeCategoryType(columnTypeMap.get(column));
    if (!operatorConfig.types.includes(columnType)) {
      return `${conditionLabel} cannot use "${condition.operator}" with a ${columnType} column.`;
    }

    for (let operandIndex = 0; operandIndex < operatorConfig.operandCount; operandIndex += 1) {
      const suffix = operandIndex === 0 ? '' : '2';
      const operandError = validateOperand(
        condition,
        suffix,
        operatorConfig,
        columnType,
        columnTypeMap,
      );
      if (operandError) return `${conditionLabel} ${operandError}.`;
    }
  }

  return null;
};

const validateCellCondition = (condition, targetType) => {
  const type = condition?.type || 'always';
  if (!CELL_CONDITION_TYPES.has(type)) return 'must select a valid condition type';
  // The editor offers only the conditions the target column supports, but a rule
  // saved before it did — or written straight to the table document — must be
  // refused here rather than at the next upload, where it fails the whole unit.
  if (!cellConditionTypesForColumn(targetType).includes(type)) {
    return `cannot use "${CELL_CONDITION_LABELS[type]}" on a ${targetType} column`;
  }
  if (type === 'content_length' || type === 'numeric_comparison') {
    const operator = condition.operator || '==';
    if (!CELL_CONDITION_OPERATORS.has(operator)) return 'must select a valid operator';
    if (!cellConditionOperators(type, targetType).includes(operator)) {
      return `cannot use "${operator}" on a ${targetType} column`;
    }
    if (type === 'content_length' && !isCharacterCount(condition.value)) {
      return 'must provide a non-negative whole number of characters, written in full (8, not 8.0)';
    }
    if (!isNumeric(condition.value)) return 'must provide a numeric value';
    return null;
  }
  if (type === 'content_type') return CONTENT_TYPES.has(condition.value) ? null : 'must select a content type';
  if (type === 'contains') return isEmptyTypedValue(condition.value) ? 'must provide the text to look for' : null;
  if (type === 'is_contained_in') {
    return Array.isArray(condition.values) && condition.values.length > 0
      ? null
      : 'must list at least one allowed value';
  }
  return null;
};

const validateTransform = (action, targetType) => {
  if (!action.transformation) return 'must select a transformation';
  const allowed = TRANSFORMS_BY_TYPE[targetType] || new Set();
  if (!allowed.has(action.transformation)) {
    return `cannot use "${action.transformation}" on a ${targetType} column`;
  }
  const params = action.transformation_params || {};
  for (const [parameter, kind, wanted] of TRANSFORM_PARAMETERS[action.transformation] || []) {
    const value = params[parameter];
    const present = kind === 'count' ? Number(value) >= 1 : !isEmptyTypedValue(value);
    if (!present) return `must provide ${wanted}`;
  }
  return null;
};

const validateArithmetic = (config, targetType, columnTypeMap) => {
  if (!config.operation) return 'must select an operation';
  if (!ARITHMETIC_OPERATIONS[targetType].includes(config.operation)) {
    return `cannot use the "${config.operation}" operation on a ${targetType} column`;
  }

  // The runtime takes the base from source_column whenever one is stored, whatever
  // source_mode says, so a stored source column is always checked.
  let sourceType = targetType;
  if (config.source_mode === 'column' && !config.source_column) return 'must select a source column';
  if (config.source_column) {
    if (!columnTypeMap.has(config.source_column)) {
      return `uses column "${config.source_column}", which is not in this table`;
    }
    sourceType = normalizeCategoryType(columnTypeMap.get(config.source_column));
    const allowedSources = targetType === 'Number' && config.operation === 'subtract'
      ? ['Number', 'Date']
      : [targetType];
    if (!allowedSources.includes(sourceType)) {
      return `source column "${config.source_column}" must be ${allowedSources.join(' or ')}`;
    }
  }

  const operandType = targetType === 'Number' && config.operation === 'subtract' && sourceType === 'Date'
    ? 'Date'
    : 'Number';
  if ((config.operand_source || 'static') === 'column') {
    if (!config.operand_column) return 'must select an operand column';
    if (!columnTypeMap.has(config.operand_column)) {
      return `uses column "${config.operand_column}", which is not in this table`;
    }
    if (normalizeCategoryType(columnTypeMap.get(config.operand_column)) !== operandType) {
      return `operand column "${config.operand_column}" must be ${operandType}`;
    }
    return null;
  }
  if (operandType === 'Date') return isEmptyTypedValue(config.operand) ? 'must provide an operand date' : null;
  if (isNumeric(config.operand)) return null;
  return targetType === 'Date' ? 'must provide a number of days' : 'must provide a numeric value';
};

const validateSetValue = (action, targetType, columnTypeMap) => {
  if (action.value_source === 'formula') {
    const formulaType = action.formula_type;
    if (!formulaType) return 'must select a formula type';
    if (!(FORMULA_TYPES_BY_TARGET[targetType] || []).includes(formulaType)) {
      return `cannot use the ${formulaType} formula type on a ${targetType} column`;
    }
    const config = action.formula_config || {};
    if (formulaType === 'prepend' || formulaType === 'append') {
      if (!config.source_column) return 'must select a source column';
      if (!columnTypeMap.has(config.source_column)) {
        return `uses column "${config.source_column}", which is not in this table`;
      }
      return null;
    }
    return formulaType === 'arithmetic' ? validateArithmetic(config, targetType, columnTypeMap) : null;
  }

  if (action.value_type === 'column') {
    if (!action.source_column) return 'must select a source column';
    if (!columnTypeMap.has(action.source_column)) {
      return `copies from column "${action.source_column}", which is not in this table`;
    }
    if (normalizeCategoryType(columnTypeMap.get(action.source_column)) !== targetType) {
      return `source column "${action.source_column}" must also be ${targetType}`;
    }
    return null;
  }

  // A fixed value: false and 0 are values, null and blank text are not (H103).
  return isEmptyTypedValue(action.value ?? action.static_value) ? 'must provide a value' : null;
};

const validateAction = (rule, columnTypeMap) => {
  const action = rule.action || {};
  if (!ACTION_TYPES.has(action.type)) return 'must select a valid action type';
  if (!TARGET_REQUIRED_ACTIONS.has(action.type)) return null;

  const targetColumn = rule.level === 'cell' ? rule.target_column : action.column;
  if (!targetColumn) return 'must select a target column';
  if (!columnTypeMap.has(targetColumn)) {
    return `targets column "${targetColumn}", which is not in this table`;
  }
  const targetType = normalizeCategoryType(columnTypeMap.get(targetColumn));
  if (TEXT_ONLY_ACTIONS.has(action.type) && targetType !== 'Text') {
    return `cannot use "${action.type}" on a ${targetType} column`;
  }

  if (action.type === 'concatenate') {
    const sources = Array.isArray(action.source_columns) ? action.source_columns : [];
    if (sources.length === 0) return 'must select at least one column to concatenate';
    const missing = sources.find(column => !columnTypeMap.has(column));
    if (missing !== undefined) return `concatenates column "${missing}", which is not in this table`;
    // Concatenate applies its transform to the joined text with no parameters, so it
    // can only run the four its own menu offers. Switching a Transform Value action to
    // Concatenate used to leave the old transformation behind — a retained "left" ran
    // with a count of zero and replaced every matched value with an empty string.
    const transformation = action.transformation || '';
    if (transformation && !CONCATENATE_TRANSFORMS.has(transformation)) {
      return `cannot use the "${transformation}" transform on a concatenate action`;
    }
    return null;
  }
  if (action.type === 'transform_value') return validateTransform(action, targetType);
  if (action.type === 'set_value') return validateSetValue(action, targetType, columnTypeMap);
  return null;
};

const ruleLabel = (rule, index) => {
  const name = String(rule?.name || '').trim();
  return name ? `Rule ${index + 1} ("${name}")` : `Rule ${index + 1}`;
};

/**
 * Return the first problem with an enabled rule, or null when every enabled
 * rule has an id, a complete condition and a complete action against the
 * current table schema. Disabled rules are drafts and are not checked.
 */
export function validateIngestionRules(rules, tableSchema) {
  const columnTypeMap = buildRuleColumnTypeMap(tableSchema);
  const rulesArray = rulesToArray(rules);

  for (let ruleIndex = 0; ruleIndex < rulesArray.length; ruleIndex += 1) {
    const rule = rulesArray[ruleIndex];
    if (rule.enabled === false) continue;

    const label = ruleLabel(rule, ruleIndex);
    // A unit's rule process and a verdict name the rule by its id (Spec 6
    // section 2.3); the editor gives every rule one.
    if (typeof rule.id !== 'string' || !rule.id.trim()) {
      return `${label} has no id. Delete it and add it again.`;
    }
    const isCellRule = rule.level === 'cell';
    if (isCellRule) {
      if (!rule.target_column) return `${label} must select a target column.`;
      if (!columnTypeMap.has(rule.target_column)) {
        return `${label} targets column "${rule.target_column}", which is not in this table.`;
      }
    }

    const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
    const usesConditions = !isCellRule || rule.use_advanced_conditions || conditions.length > 0;
    if (usesConditions) {
      const conditionError = validateConditions(conditions, label, columnTypeMap);
      if (conditionError) return conditionError;
    } else {
      const targetType = normalizeCategoryType(columnTypeMap.get(rule.target_column));
      const conditionError = validateCellCondition(rule.cell_condition, targetType);
      if (conditionError) return `${label} cell condition ${conditionError}.`;
    }

    const actionError = validateAction(rule, columnTypeMap);
    if (actionError) return `${label} action ${actionError}.`;
  }

  return null;
}
